/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, DoCheck, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './folio-information-page.component.html';

import {creativeSetListCommons} from '../creative-set/creative-set-commons';
import {creativeListCommons} from '../creative/creative-commons';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {CreativeSetResource} from '../../components/api/resources/resource/creative-set.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-folio-information-page',
  template
})
export class FolioInformationPageComponent implements DoCheck, OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'creativeSets' | 'creatives' = 'creativeSets';
  showCreatives = false;

  creativeSets: any = {};
  creatives: any = {};

  private lastModelId: string | undefined;
  private subscribedModelComms: any;

  constructor(
    @Inject(CreativeSetResource) private readonly CreativeSet: any,
    @Inject(CreativeResource) private readonly Creative: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly $state: StateService,
    private ngZone: NgZone
  ) {
    this.showCreatives = !!LocalUserPermissions.getSectionPerms().creatives;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId !== this.lastModelId) {
      this.lastModelId = modelId;
      this.initLists();
    }
  }

  selectTab(tab: FolioInformationPageComponent['activeTab']): void {
    this.activeTab = tab;
  }

  loadExternalCreativeSet(): void {
    this.$state.go('app.adv.creative-sets.new', {
      folioId: this.model.id,
      folioName: this.model.name,
      advertiserId: _.get(this.model, ['advertiser', 'id']),
      advertiserName: _.get(this.model, ['advertiser', 'name'])
    });
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', _.assign({}, advertiserNavigationParams(this.model.advertiser), {
      folioId: this.model.id,
      folioName: this.model.name
    }));
  }

  newCreative(): void {
    this.$state.go('app.adv.creatives.new', _.assign({}, advertiserNavigationParams(this.model.advertiser), {
      folioId: this.model.id,
      folioName: this.model.name
    }));
  }

  private initLists(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.creativeSets = {};
      this.creatives = {};
      return;
    }

    this.creativeSets = {};
    this.adnListHelper.setUpBasicList(this.creativeSets, this.CreativeSet, 'fullCreativeSetsForFolio', {
      moreParams: {folio: this.model.id},
      baseTable: 'creativeSet',
      afterFill: () => {
        this.ngZone.run(() => {
          creativeSetListCommons(this.creativeSets, true, this.LocalNetworkProfile, this.adnTimeShifter);
        });
      }
    });
    this.creativeSets.perms = this.LocalUserPermissions.getAllVisPermissions();
    creativeSetListCommons(this.creativeSets, false, this.LocalNetworkProfile, this.adnTimeShifter);

    this.creatives = {};
    this.adnListHelper.setUpBasicList(this.creatives, this.Creative, 'fullCreativesForFolio', {
      moreParams: {folio: this.model.id},
      baseTable: 'creative',
      afterFill: () => {
        this.ngZone.run(() => {
          creativeListCommons(this.creatives, true, this.LocalNetworkProfile, this.$state);
        });
      }
    });
    creativeListCommons(this.creatives, false, this.LocalNetworkProfile, this.$state);
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any) => {
      if (type || !data) {
        return;
      }
      this.ngZone.run(() => {
        if (data.name !== undefined && this.model) {
          this.model.name = data.name;
        }
        if (data.objectState !== undefined && this.model) {
          this.model.objectState = data.objectState;
        }
        if (data.validationWarnings !== undefined && this.model) {
          this.model.validationWarnings = _.cloneDeep(data.validationWarnings);
        }
        if (data.name && this.effectiveIsNew) {
          this.effectiveIsNew = false;
          this.initLists();
        } else if (data.objectState !== undefined) {
          this.refreshEmbeddedLists();
        }
      });
    });
  }

  private refreshEmbeddedLists(): void {
    if (_.isFunction(this.creativeSets?.makeQuery)) {
      this.creativeSets.makeQuery();
    }
    if (_.isFunction(this.creatives?.makeQuery)) {
      this.creatives.makeQuery();
    }
  }
}
